// Mirror of backend DTOs (com.myenglishvocabulary.model.dto.feedback). No codegen — keep in sync by hand.

export type FeedbackType = 'BUG' | 'REVIEW' | 'IDEA';
export type FeedbackStatus =
  | 'NEW' | 'PLANNED' | 'IN_PROGRESS' | 'RESOLVED' | 'DECLINED' | 'DUPLICATE';
export type BugArea = 'BACKEND' | 'FRONTEND' | 'CONTENT';
export type BugSeverity = 'LOW' | 'MEDIUM' | 'HIGH';

export interface BugContext {
  route: string;
  version: string;
  browser: string;
  os: string;
  locale: string;
  viewport: string;
  errors: string[];
}

export interface CreateFeedbackRequest {
  type: FeedbackType;
  title: string;
  description: string;
  bugArea?: BugArea;
  severity?: BugSeverity;
  relatedEntity?: string;
  rating?: number;
  context?: BugContext;
}

export interface FeedbackTO {
  id: number;
  type: FeedbackType;
  status: FeedbackStatus;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  bugArea?: BugArea;
  severity?: BugSeverity;
  relatedEntity?: string;
  rating?: number;
  voteCount?: number;
  public: boolean; // Jackson serializes the `isPublic` boolean as "public"
}

// Spring Page<T> envelope (matches other paginated endpoints in this app)
export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface IdeaTO {
  id: number;
  title: string;
  description: string;
  status: FeedbackStatus;
  voteCount: number;
  votedByMe: boolean;
  authorName: string;
  createdAt: string;
}

// --- view label maps (Ukrainian) ---
export const TYPE_LABEL: Record<FeedbackType, string> = {
  BUG: 'Баг',
  REVIEW: 'Відгук',
  IDEA: 'Ідея',
};

export const BUG_AREA_LABEL: Record<BugArea, string> = {
  BACKEND: 'Бекенд',
  FRONTEND: 'Інтерфейс',
  CONTENT: 'Контент',
};

export const SEVERITY_LABEL: Record<BugSeverity, string> = {
  LOW: 'Низький',
  MEDIUM: 'Середній',
  HIGH: 'Високий',
};

export interface StatusStyle {
  label: string;
  bg: string;
  fg: string;
  dot: string;
}

export const STATUS_STYLE: Record<FeedbackStatus, StatusStyle> = {
  NEW: { label: 'Нове', bg: '#E7E7E7', fg: '#4a4f57', dot: '#7A7F88' },
  PLANNED: { label: 'Заплановано', bg: 'rgba(59,44,147,.10)', fg: '#3B2C93', dot: '#3B2C93' },
  IN_PROGRESS: { label: 'В роботі', bg: 'rgba(255,193,7,.16)', fg: '#96690a', dot: '#FFC107' },
  RESOLVED: { label: 'Вирішено', bg: 'rgba(39,204,188,.16)', fg: '#12897d', dot: '#27CCBC' },
  DECLINED: { label: 'Відхилено', bg: 'rgba(226,51,10,.10)', fg: '#E2330A', dot: '#E2330A' },
  DUPLICATE: { label: 'Дублікат', bg: 'rgba(119,123,255,.14)', fg: '#5a5fd6', dot: '#777BFF' },
};

export interface AdminFeedbackTO {
  id: number;
  type: FeedbackType;
  status: FeedbackStatus;
  title: string;
  description: string;
  authorName: string;
  authorEmail?: string;
  createdAt: string;
  updatedAt: string;
  bugArea?: BugArea;
  severity?: BugSeverity;
  relatedEntity?: string;
  rating?: number;
  voteCount?: number;
  context?: string;
  adminNote?: string;
  duplicateOfId?: number;
}

export interface AdminPatchRequest {
  status?: FeedbackStatus;
  adminNote?: string;
  duplicateOfId?: number;
}

export const ALL_STATUSES: FeedbackStatus[] = ['NEW', 'PLANNED', 'IN_PROGRESS', 'RESOLVED', 'DECLINED', 'DUPLICATE'];
